import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Typography, Flex, Select } from '@app/ui';
import {
    AGE_BRACKET_LABEL_KEYS,
    DEMOGRAPHIC_AGE_BRACKETS,
    DEMOGRAPHIC_GENDERS,
    GENDER_LABEL_KEYS,
} from '@app/constants/demographics';
import { FiltersFormContext } from './FiltersFormContext';
import { FilterValues } from '../types';

const selectStyle = { maxHeight: '32px', overflow: 'hidden' };

const DemographicsFiltersRow = () => {
    const { t } = useTranslation();
    const { values, setFieldValue } = useFormikContext<FilterValues>();
    const { isOverridingFilters } = useContext(FiltersFormContext) ?? {};

    const ageBracketOptions = useMemo(
        () =>
            DEMOGRAPHIC_AGE_BRACKETS.map(value => ({
                value,
                label: t(AGE_BRACKET_LABEL_KEYS[value]),
            })),
        [t],
    );

    const genderOptions = useMemo(
        () =>
            DEMOGRAPHIC_GENDERS.map(value => ({
                value,
                label: t(GENDER_LABEL_KEYS[value]),
            })),
        [t],
    );

    const update = (key: 'ageBrackets' | 'genders') => (value: string[]) =>
        setFieldValue(key, value, false);

    return (
        <Flex
            vertical
            gap={8}
            style={{ padding: '16px 0' }}
            data-test="demographic-filters-row"
        >
            <Flex gap={16} wrap="wrap">
                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="age-bracket-dropdown"
                    >
                        {t('filters.ageBracket')}
                    </Typography.Text>
                    <Select
                        mode="multiple"
                        placeholder={t('filters.selectAgeBrackets')}
                        options={ageBracketOptions}
                        value={values.ageBrackets ?? []}
                        onChange={update('ageBrackets')}
                        optionFilterProp="label"
                        allowClear
                        maxTagCount={2}
                        maxTagPlaceholder="..."
                        style={selectStyle}
                        disabled={isOverridingFilters}
                        className="-filter-select"
                    />
                </Flex>
                <Flex vertical flex={1} gap={4}>
                    <Typography.Text
                        className="-filter-title"
                        data-test="gender-dropdown"
                    >
                        {t('filters.gender')}
                    </Typography.Text>
                    <Select
                        mode="multiple"
                        placeholder={t('filters.selectGenders')}
                        options={genderOptions}
                        value={values.genders ?? []}
                        onChange={update('genders')}
                        optionFilterProp="label"
                        allowClear
                        maxTagCount={2}
                        maxTagPlaceholder="..."
                        style={selectStyle}
                        disabled={isOverridingFilters}
                        className="-filter-select"
                    />
                </Flex>
            </Flex>
        </Flex>
    );
};

export default DemographicsFiltersRow;
